import {
  TravelInsuranceType as GeneratedTravelInsuranceType,
  TravelInsuranceProductDetailForCheckout,
  TravelInsuranceType,
  TravellerInput,
} from '__generated__/graphql';
import type { PolicySummaryPurchase } from './policySummary';
import {
  FETCH_PREMIUM_STATE,
  RELATIONSHIP_WITH_POLICYHOLDER,
} from '$/(application)/travel-insurance/checkout/[carrierCode]/constants';

export interface TripDetails {
  insuranceType: GeneratedTravelInsuranceType;
  destinations: string[];
  startDate: string; // ISO date format (e.g., "2024-09-17")
  endDate: string; // ISO date format (e.g., "2024-09-19")
  region?: string;
  travelAsFamily: string; // "true" | "false"
  travellers: (Traveller | TravellerInput)[];
}

export interface AdditionalTravellerDetails {
  dateOfBirth: string;
  firstName: string;
  gender: string;
  identityNumber: string;
  identityType: IdentityType;
  lastName: string;
  mainlandPermitNumber?: string;
  nationality: string;
  placeOfResidence: string;
  // GE only, derived from `InsuredDetails.travellerAddOns`. The backend
  // takes it per traveller, as a boolean.
  preExisting?: boolean;
  relationWithPolicyholder: string;
  title: string;
}

export type YesNo = 'Yes' | 'No';

export interface InsuredDetails {
  address: {
    block?: string;
    buildingName?: string;
    city?: string;
    estate?: string;
    floor?: string;
    phase?: string;
    postCode?: string;
    primaryAddressSingleLine?: string;
    state?: string;
    streetName?: string;
    streetNo?: string;
    unitNumber?: string;
  };
  coverChinaNetworkHospitalAdmission?: string;
  coverCruiseVacation?: string;
  coverGolfProtection?: string;
  coverMandatoryCoverage?: string;
  coverScubaDiving?: string;
  coverSnowSports?: string;
  dateOfBirth: string;
  email: string;
  email2: string;
  firstName: string;
  gender: string;
  identityNumber: string;
  identityType: IdentityType;
  // GE only: whether the policyholder is also a traveller on this trip. Yes:
  // the policyholder is Traveller 1 and `travellers` is Traveller 2..n. No:
  // the policyholder only buys, and `travellers` is Traveller 1..n.
  isProposer?: YesNo;
  lastName: string;
  mainlandPermitNumber?: string;
  maritalStatus: string;
  marketingConsent: string;
  nationality: string;
  phoneNumber: string;
  placeOfResidence: string;
  // GE only, FE state: 'true' when any traveller has the add-on (plan card,
  // footer). Not sent at policy level; the backend reads it per traveller.
  preExisting?: string;
  race: string;
  relationWithPolicyholder: string;
  title: string;
  // Per-traveller add-ons (GE): add-on technicalId -> selected traveller
  // numbers, 1-based in block order (Traveller 1 first).
  travellerAddOns?: Record<string, number[]>;
  travellers: Partial<AdditionalTravellerDetails>[];
}

export type RelationshipWithPolicyHolder =
  (typeof RELATIONSHIP_WITH_POLICYHOLDER)[keyof typeof RELATIONSHIP_WITH_POLICYHOLDER];

export interface Traveller {
  relationship: RelationshipWithPolicyHolder;
  dateOfBirth: string; // ISO date format (e.g., "2006-09-17")
}

export enum Gender {
  FEMALE = 'F',
  MALE = 'M',
}

export enum MaritalStatus {
  SINGLE = 'S',
  MARRIED = 'M',
  OTHERS = 'O',
  WIDOWED = 'W',
  DIVORCED = 'D',
}

export enum RaceCode {
  CHINESE = 'CHI',
  MALAY = 'MAL',
  INDIAN = 'IND',
  OTHERS = 'OTH',
}

export enum IdentityType {
  NRIC = 'NRIC',
  FIN = 'FIN',
  PASSPORT = 'PASSPORT',
  HKID = 'HKID',
  NONE = '',
}

export const RelationsWithPolicyholderMap = {
  SLF: 'SELF',
  SP: 'SPOUSE',
  FRND: 'COMPANION',
  PRNT: 'COMPANION',
  SBLNG: 'COMPANION',
  CHLD: 'CHILD',
  REL: 'COMPANION',
  CLG: 'COMPANION',
  EMPL: 'COMPANION',
  EMPLR: 'COMPANION',
  OTH: 'COMPANION',
};

export type AddressResult = {
  SEARCHVAL: string;
  BLK_NO: string;
  ROAD_NAME: string;
  BUILDING: string;
  ADDRESS: string;
  POSTAL: string;
};

export enum CarrierCode {
  ALWD = 'ALLIEDWORLD',
  BLCR = 'BLCR',
  BOLTTECH = 'BOLTTECH',
  ETIQA = 'ETIQA',
  FWD = 'FWD',
  GENX = 'GENERALI',
  // Great Eastern TravelCare E2E (INS-6550), code registered by the backend.
  GE = 'GE',
  HLAS = 'HLAS',
  MHALLIANZ = 'MHALLIANZ',
  MHBOLTTECH = 'MHBOLTTECH',
  MHGENERALI = 'MHGENERALI',
  MSIG = 'MSIG',
  QBE = 'QBE',
  SSALLIANZ = 'SSALLIANZ',
  SSZURICH = 'SSZURICH',
  SSHLAS = 'SSHLAS',
  STAR = 'STARR',
  NONE = '',
  ALL = 'ALL',
}

export enum CountryCode {
  SG = 'SG',
  HK = 'HK',
  CN = 'CN',
  IN = 'IN',
  JP = 'JP',
  MY = 'MY',
  TH = 'TH',
  TW = 'TW',
  NONE = '',
  ALL = 'ALL',
}

export const MAP_BRAND_TO_COUNTRY_CODE = {
  singsaver: 'SG',
  moneyhero: 'HK',
};

export enum Currency {
  SG = 'SGD',
  HK = 'HKD',
  NONE = '',
}

/**
 * The ISO currency code TI prices and their tracking report for a brand (SGD, HKD). Brands that
 * render no travel-insurance page resolve to ''.
 */
export const tiCurrencyCodeOf = (brand: string): string => {
  const countryCode =
    MAP_BRAND_TO_COUNTRY_CODE[brand as keyof typeof MAP_BRAND_TO_COUNTRY_CODE];
  return Currency[countryCode as keyof typeof Currency] ?? '';
};

export type StripePaymentLocale = 'EN' | 'ZH_HK';

export enum VerticalCode {
  CI = 'CI',
  TI = 'TI',
  NONE = '',
  ALL = 'ALL',
}

export type RulesAppContextConfig = {
  carrierCode?: CarrierCode[];
  // Carriers the rule does not apply to (e.g. every carrier but GE).
  excludeCarrierCode?: CarrierCode[];
  market: CountryCode;
  insuranceType?: TravelInsuranceType;
};

export type RulesFieldsConfig = {
  /**
   * If true, the field will be shown on FE
   */
  show?: RulesAppContextConfig[];
  /**
   * if true, the field is readonly on FE
   */
  disabled?: RulesAppContextConfig[];
  /**
   * if true, the field is optional on FE
   * and the validation will only kick in
   * if there's a value entered on the field
   */
  optional?: RulesAppContextConfig[];
};

export type RulesFieldsets<T> = {
  insuredDetails: {
    title: T;
    firstName: T;
    lastName: T;
    identityType: T;
    identityNumber: T;
    nationality: T;
    maritalStatus: T;
    relationWithPolicyholder: T;
    dateOfBirth: T;
    placeOfResidence: T;
    email: T;
    email2: T;
    phoneNumber: T;
    gender: T;
    race: T;
  };
  address: {
    block: T;
    buildingName: T;
    estate: T;
    phase: T;
    streetName: T;
    postCode: T;
    unitNumber: T;
    city: T;
    state: T;
    floor: T;
    streetNo: T;
    primaryAddressSingleLine: T;
  };
  travelers: {
    title: T;
    firstName: T;
    lastName: T;
    dateOfBirth: T;
    relationWithPolicyholder: T;
    placeOfResidence: T;
    relationship: T;
    nationality: T;
    gender: T;
    identityType: T;
    identityNumber: T;
    phoneNumber: T;
  };
  optionalcover: {
    coverChinaNetworkHospitalAdmission: T;
  };
};

/**
 * This type is for field controls availability
 *    configuration.
 * It plots the criteria of fields availability on the form
 */
export type RulesFieldsetsConfig = RulesFieldsets<RulesFieldsConfig>;

export type RulesFieldsResolution = {
  /**
   * If true, the field will be shown on FE
   */
  show: boolean;
  /**
   * if true, the field is readonly on FE
   */
  disabled: boolean;
  /**
   * if true, the field is optional on FE
   * and the validation will only kick in
   * if there's a value entered on the field
   */
  optional: boolean;
};

export type RulesFieldsetsResolution = RulesFieldsets<RulesFieldsResolution>;

export type AppFormContext = {
  countryCode: CountryCode;
  carrierCode: CarrierCode;
  insuranceType: TravelInsuranceType;
};

export type RulesFieldsetsContextValue = {
  rulesFieldsetsConfig: RulesFieldsetsResolution;
};

export type TravelInsuranceTypeCode = 'ANL' | 'SGL';

export interface DobLimit {
  feMaxAge?: number;
  feMaxAgeUnit?: string;
  feMinAge?: number;
  feMinAgeUnit?: string;
  max?: Date;
  min?: Date;
}

export interface TravellerDobLimit {
  adult?: DobLimit;
  child?: DobLimit;
  other?: DobLimit;
  policyHolder?: DobLimit;
}

export type CalcBasis = 'EffectiveDate' | 'ExpiryDate' | 'CalcRequestTime';

export type ProductOptionsValues = {
  cmsProductId: string; //from content stack
  coverChinaNetworkHospitalAdmission?: string;
  coverCruiseVacation?: string;
  coverGolfProtection?: string;
  coverMandatoryCoverage?: string;
  coverScubaDiving?: string;
  coverSnowSports?: string;
  destinations: string[]; //"TH"
  endDate: string; //2022-03-10T12:15:50-04:00,
  insuranceType: TravelInsuranceTypeCode;
  lang: string; //"en-sg"
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  policyCoverages?: any; //for FWD-TI
  productId: string; //from content stack
  productName: string; //from content stack
  startDate: string; //2022-03-10T12:15:50-04:00,
  travelAsFamily?: string; //"true" or "false"
  travellers: Partial<AdditionalTravellerDetails>[];
};

export type SubmitApplicationPayload = {
  insuredDetailsValues: {
    basePremium: number;
    brokerDiscountAmount: string;
    calculateRequestId: string;
    collectionAmount: number;
    dateOfBirth: string; // 2022-03-10,
    discountAmount: number;
    duePremium: number | undefined;
    marketingconsent: string;
    mainlandPermitNumber?: string;
    originalPremium: number;
    planCode: string;
    premiumBeforeTax?: number;
    referenceId: string;
    relationWithPolicyholder: string; // SLF
    rewardconsent: string; // "true" or "false"
    storeConsentInInsureMo?: boolean;
    totalAddonPremium?: number;
  } & InsuredDetails;
  productOptionsValues: ProductOptionsValues;
  origin: string | undefined;
};

export type SubmitApplicationResponse = {
  applicationId: string;
  clientRequestId: string;
  quotationNumber: string;
  sanctionCheckPass: boolean;
  isManualWriting?: boolean;
};

export enum PaymentCheckoutStatus {
  PENDING = 'PENDING',
  PAID = 'PAID',
  ERROR = 'ERROR',
  SUBMITTED = 'SUBMITTED',
  SUCCESS = 'SUCCESS',
  NONE = '',
}

export type PaymentDetails = {
  applicationId?: string;
  finalPremium?: string;
  insuredEmail?: string;
  insuredName?: string;
  paymentStatus?: PaymentCheckoutStatus;
  planProductId?: string;
  policyNumber?: string;
  quotationNumber?: string;
  requestId?: string;
  sessionId?: string;
};

/**
 * Response of `GET /api/insurance/v1/data/policy-summary?applicationId=…`
 * (guest-token auth) — the authoritative post-purchase record that backs the
 * TI E2E purchase-success page (CMS-1794). Shape mirrors the current HubSpot
 * consumer (`comparison-ui-hubspot/.../TICheckoutSuccess`); reading the same
 * source keeps the confirmation page consistent with Review (CMS-1794 AC-5).
 */
export type PolicySummaryResponse = {
  applicationId: string;
  basePremium: number;
  carrierCode: string;
  carrierName: string;
  collectionAmount: number;
  coverageType: string;
  coverChinaNetworkHospitalAdmission: boolean;
  destinations: string[];
  duePremium: number;
  endDate: string;
  insureMoDiscountAmount: number;
  isManualWriting: boolean;
  marketingConsent: boolean;
  noOfAdults: number;
  noOfChildren: number;
  planType: TravelInsuranceTypeCode;
  policyNo: string;
  productCode: string;
  productImage: string;
  productName: string;
  quotationNumber: string;
  selectedAddOns: string[];
  startDate: string;
  stripeDiscountAmount: number;
  travelAsFamily: boolean;
  travelPeriod: number;
  regionDisclaimer?: string[];
};

// Client-resolved state of the TI purchase-success page (CMS-1794). Computed by
// `usePolicySummary` from the `application-id` query param + the localStorage
// purchase snapshot + the policy-summary API, and published into the page
// context slice for the confirmation blocks to read.
export type PolicySummaryStatus = 'loading' | 'invalid' | 'ready';

export type PolicySummaryState = {
  status: PolicySummaryStatus;
  // Manual-underwriting / "application under review" (data.isManualWriting).
  // Drives the pending heading + delivery note; only meaningful when ready.
  isPending: boolean;
  // First name from the localStorage snapshot (never from the API).
  firstName?: string;
  // The policy-summary payload; present only when `status === 'ready'`.
  data?: PolicySummaryPurchase;
};

export type PaymentCheckoutPayload = {
  amount?: number;
  applicationId?: string;
  cancelUrl?: string;
  carrierCode: CarrierCode;
  compareQuoteRequestId?: string;
  countryCode: CountryCode;
  currency?: string;
  isManualWriting?: boolean;
  locale?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata?: any;
  paymentDetails?: PaymentDetails;
  productName?: string;
  requestId?: string;
  sessionId?: string;
  successUrl?: string;
};

export enum CoverageType {
  INDIVIDUAL = 'INDV',
  FAMILY = 'FAM',
  GROUP = 'GRP',
  CPL = 'COUPLE',
  ADULTANDCHILDREN = 'ANC',
  NONE = '',
}

export enum PolicyProductCode {
  SINGSAVER_CAR_INSURANCE = 'PCAR001',
  SINGSAVER_TRAVEL_INSURANCE = 'TRVL001',
  MONEYHERO_CAR_INSURANCE = 'PCAR002',
  NONE = '',
}

export type CarrierPremiumCalculation = {
  basePremium?: number;
  brokerDiscountAmount?: string;
  brokerDiscountType?: string;
  brokerProductCode: string;
  carrierName?: string;
  clientRequestId?: string;
  collectionAmount?: number;
  coverageType?: CoverageType;
  currencyCode?: string;
  discountAmount?: number;
  discountType?: string;
  discountValue?: number;
  displayedDuePremium?: number; // calculated on FE
  duePremium?: number;
  effectiveDate?: string;
  extProductCode?: string;
  expiryDate?: string;
  golfCoverPremium?: number;
  mandatoryCoverPremium?: number;
  maintenance?: boolean;
  originalPremium?: number;
  planCode?: string;
  planDesc?: string;
  planName?: string;
  planProductCode?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  policyCoverageList: any;
  // GE: pre-existing conditions cover for every insured person on the quote.
  preExistingBenefitPremium?: number;
  premiumBeforeTax: number;
  productCode?: PolicyProductCode;
  productVersion?: string;
  quotationExpiryDate?: string;
  referenceId?: string;
  scubaDivingCoverPremium?: number;
  snowSportsCoverPremium?: number;
  taxAmount?: number;
  taxPercent?: number;
  totalAddonPremium?: number;
  totalDiscountAmount: number;
  totalDiscountPercent: number;
};

export type AddOn = {
  // Free text under the benefit table (GE: exclusions and availability note),
  // one paragraph per line.
  additionalInformation?: string;
  description: string;
  itemDetails: {
    additionalInformation: string;
    description: string;
    label: string;
    value: string;
  }[];
  label: string;
  prefix: string;
  technicalId: string;
  value: string;
};

export type ProductDetailsForCheckout = Partial<
  TravelInsuranceProductDetailForCheckout & {
    addOns?: AddOn[];
    brochureDocumentLink?: string;
    coverageBenefitAttributes: {
      additionalInformation?: string;
      attributeFormat?: string;
      attributeType?: string;
      label?: string;
      value?: string;
    }[];
    entryUid: string;
    insuremoProductId: string;
    isSinglePlanProduct: boolean;
    productImageUrl: string;
    productName: string;
    productUid: string;
    providerName: string;
    providerUid: string;
    regionDisclaimer?: string;
    tierLevel: number;
    wordingDocumentLink?: string;
    marketingConsentText?: string;
    storeConsentInInsureMo?: boolean;
  }
>;

export type ScaleoParameters = {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  clkid: string;
  clktype: string;
  rewards: string;
};

export type FetchPremiumState = {
  status: FETCH_PREMIUM_STATE;
  payload?: unknown;
};
